// Unified action budget. Activities cost time and/or energy.
// Scenes call canPerform(actionId) before starting an activity and spendForAction
// after it begins. Sleeping/napping restores energy.

import { getState, save } from './save.js';
import { advanceTime, getHour, isDaytime } from './world.js';

export const ACTIONS = {
  woodcut:   { timeCost: 2,   energyCost: 15, label: 'chop wood' },
  farmWater: { timeCost: 0.25, energyCost: 3,  label: 'water crops' },
  farmPlant: { timeCost: 0.5,  energyCost: 5,  label: 'plant seeds' },
  farmHarvest:{ timeCost: 0.25, energyCost: 4,  label: 'harvest crops' },
  orderRush: { timeCost: 1,   energyCost: 12, label: 'work a dinner rush' },
  grillFlip: { timeCost: 1,   energyCost: 12, label: 'work the grill' },
  recUse:    { timeCost: 0.5,  energyCost: 8,  label: 'use gym equipment' },
  dojoTrain: { timeCost: 0.5,  energyCost: 8,  label: 'train at the dojo' },
  parkPlant: { timeCost: 0.25, energyCost: 3,  label: 'plant in the park' },
  fish:      { timeCost: 1.5,  energyCost: 10, label: 'go fishing' },
  casinoPlay:{ timeCost: 0.5,  energyCost: 4,  label: 'play a casino game' },
  sleep:     { timeCost: 0,    energyCost: -100,label: 'sleep' },
  nap:       { timeCost: 0,    energyCost: -40, label: 'take a nap' }
};

const MAX_ENERGY = 100;

export function getEnergy(){ return getState().energy ?? MAX_ENERGY; }

export function setEnergy(n){
  const s = getState();
  s.energy = Math.max(0, Math.min(MAX_ENERGY, n));
  save();
}

export function restoreEnergy(n){
  setEnergy(getEnergy() + n);
}

export function spendEnergy(n){
  setEnergy(getEnergy() - n);
}

export function getAction(id){ return ACTIONS[id]; }

export function canPerform(actionId, options = {}){
  const def = ACTIONS[actionId];
  if (!def) return { ok: false, reason: 'unknown action' };

  const s = getState();
  const energy = getEnergy();
  const ignoreEnergy = options.ignoreEnergy ?? false;

  if (!ignoreEnergy && energy < def.energyCost){
    return { ok: false, reason: `too tired (need ${def.energyCost} energy)` };
  }

  // Prevent sleep during the day unless forced (e.g. house bed).
  if (actionId === 'sleep' && isDaytime() && !options.allowDaySleep){
    return { ok: false, reason: "not tired enough to sleep during the day" };
  }

  return { ok: true };
}

// Spend the action's time and energy. Returns canPerform result if blocked.
export function spendForAction(actionId, options = {}){
  const check = canPerform(actionId, options);
  if (!check.ok) return check;

  const def = ACTIONS[actionId];
  if (def.timeCost > 0) advanceTime(def.timeCost);
  if (def.energyCost > 0) spendEnergy(def.energyCost);
  else if (def.energyCost < 0) restoreEnergy(Math.abs(def.energyCost));

  return { ok: true };
}

export function actionLabel(actionId){
  return ACTIONS[actionId]?.label || actionId;
}
